import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Ship, MapPin, Calendar, Anchor, Sparkles, ArrowRight, Loader2 } from "lucide-react";
import { useI18n } from "@/i18n";

// Robust boat image mapping — accepts IT/EN/lowercase variants.
// Each category has a few options to avoid CDN failures showing wrong-genre photos.
const BOAT_IMAGES = {
  sailboat: [
    "https://images.unsplash.com/photo-1500627964684-141351970a7f?auto=format&fit=crop&w=1200&q=70",
    "https://images.unsplash.com/photo-1473186578172-c141e6798cf4?auto=format&fit=crop&w=1200&q=70",
    "https://images.unsplash.com/photo-1505839673365-e3971f8d9184?auto=format&fit=crop&w=1200&q=70",
  ],
  catamaran: [
    "https://images.unsplash.com/photo-1583200771217-8b9112459344?auto=format&fit=crop&w=1200&q=70",
    "https://images.unsplash.com/photo-1593351415075-3bac9f45c877?auto=format&fit=crop&w=1200&q=70",
    "https://images.unsplash.com/photo-1535314799572-930a78482c39?auto=format&fit=crop&w=1200&q=70",
  ],
  yacht: [
    "https://images.unsplash.com/photo-1605281317010-fe5ffe798166?auto=format&fit=crop&w=1200&q=70",
    "https://images.unsplash.com/photo-1542902093-d55926049754?auto=format&fit=crop&w=1200&q=70",
    "https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?auto=format&fit=crop&w=1200&q=70",
  ],
  motorboat: [
    "https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?auto=format&fit=crop&w=1200&q=70",
  ],
  gommone: [
    "https://images.unsplash.com/photo-1502209524164-acea936639a2?auto=format&fit=crop&w=1200&q=70",
  ],
};

const BOAT_TYPE_ALIASES = {
  "barca a vela": "sailboat", "sailboat": "sailboat", "vela": "sailboat",
  "catamarano": "catamaran", "catamaran": "catamaran", "cata": "catamaran",
  "yacht a motore": "yacht", "yacht": "yacht", "luxury yacht": "yacht", "motor yacht": "yacht",
  "motoscafo": "motorboat", "motorboat": "motorboat", "barca a motore": "motorboat",
  "gommone premium": "gommone", "gommone": "gommone", "rib": "gommone",
};

function normalizeBoatType(value) {
  if (!value) return "sailboat";
  const v = String(value).trim().toLowerCase();
  return BOAT_TYPE_ALIASES[v] || "sailboat";
}

function pickFromArray(arr, key) {
  if (!arr || !arr.length) return null;
  const hash = String(key || "").split("").reduce((s, c) => s + c.charCodeAt(0), 0);
  return arr[hash % arr.length];
}

function proposalImage(p) {
  // Priority: explicit image_url > deterministic pick from correct-category gallery > sailboat fallback.
  if (p?.image_url) return p.image_url;
  const cat = normalizeBoatType(p?.boat_type || p?.boat_category);
  const gallery = BOAT_IMAGES[cat] || BOAT_IMAGES.sailboat;
  return pickFromArray(gallery, p?.id || p?.boat_model || cat) || BOAT_IMAGES.sailboat[0];
}

function fallbackImageFor(p) {
  // For onError handlers: always return another in-category image, NEVER a wrong-genre one.
  const cat = normalizeBoatType(p?.boat_type || p?.boat_category);
  const gallery = BOAT_IMAGES[cat] || BOAT_IMAGES.sailboat;
  return gallery[gallery.length - 1];
}

function fmtEur(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
}

export default function ProposalPortal() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { lang } = useI18n();
  const EN = lang === "en";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submittingId, setSubmittingId] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get(`/public/proposal/${token}`);
        if (!cancel) setData(r.data);
      } catch (e) {
        if (!cancel) setNotFound(true);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [token]);

  const confirmInterest = async (proposalId) => {
    setSubmittingId(proposalId);
    try {
      await api.post(`/public/proposal/${token}/interest`, { proposal_id: proposalId });
      const base = EN ? `/proposal/${token}/confirmed` : `/proposta/${token}/conferma`;
      navigate(base, { state: { confirmed: true } });
    } catch (e) {
      alert(EN ? "Something went wrong. Please retry or write to info@seamatch.pro" : "Si è verificato un errore. Riprova o scrivici a info@seamatch.pro");
      setSubmittingId(null);
    }
  };

  if (loading) {
    return <div className="min-h-screen pt-32 text-center text-ink">
      <Loader2 className="h-6 w-6 mx-auto animate-spin text-navy" />
      <p className="mt-3">{EN ? "Loading your proposals…" : "Carichiamo le tue proposte…"}</p>
    </div>;
  }

  if (notFound || !data) {
    return (
      <div data-testid="portal-not-found" className="min-h-screen pt-32 pb-20">
        <div className="max-w-2xl mx-auto px-5 text-center">
          <h1 className="font-serif text-3xl text-navy">{EN ? "Portal not available" : "Portale non disponibile"}</h1>
          <p className="mt-3 text-ink/70">
            {EN
              ? <>The link may have expired or be invalid. Contact us at <a href="mailto:info@seamatch.pro" className="text-navy underline">info@seamatch.pro</a> for assistance.</>
              : <>Il link potrebbe essere scaduto o non valido. Contattaci a <a href="mailto:info@seamatch.pro" className="text-navy underline">info@seamatch.pro</a> per assistenza.</>}
          </p>
        </div>
      </div>
    );
  }

  const name = data.client_name || (EN ? "there" : "ciao");
  const alreadyConfirmedId = data.already_interested_id;

  return (
    <div data-testid="proposal-portal" className="bg-white">
      {/* HERO */}
      <section className="relative pt-32 pb-12">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-sand via-white to-white" />
        <div className="max-w-5xl mx-auto px-5 lg:px-10">
          <p className="label-eyebrow">{EN ? "Seamatch personal portal" : "Portale personale Seamatch"}</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl text-navy tracking-tight leading-[1.05]">
            {EN ? `Hi ${name}, your` : `Ciao ${name}, le tue`}
            <br />
            <em className="italic text-gold font-medium">{EN ? "proposals are ready." : "proposte sono pronte."}</em>
          </h1>
          <p className="mt-6 max-w-2xl text-base lg:text-lg text-ink leading-relaxed">
            {EN
              ? <>We have hand-picked {data.proposals.length === 1 ? "one option" : `${data.proposals.length} options`} aligned with your brief. When you find the one you prefer, click <strong className="text-navy">«I'm interested in this proposal»</strong>: we will contact you within 24 hours to finalise the booking with the charter company.</>
              : <>Abbiamo selezionato {data.proposals.length === 1 ? "una proposta compatibile" : `${data.proposals.length} proposte compatibili`} con la tua richiesta. Quando trovi quella che ti convince, clicca <strong className="text-navy">«Mi interessa questa proposta»</strong>: ti contatteremo entro 24 ore per finalizzare la prenotazione con la società charter.</>}
          </p>
        </div>
      </section>

      {/* PROPOSALS */}
      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-5 lg:px-10 space-y-6">
          {data.proposals.length === 0 && (
            <p className="text-center text-ink/70 py-12">{EN ? "No proposals available at the moment." : "Nessuna proposta disponibile al momento."}</p>
          )}
          {data.proposals.map((p, idx) => {
            const isConfirmed = p.client_interested || alreadyConfirmedId === p.id;
            return (
              <article
                key={p.id}
                data-testid={`portal-proposal-${p.id}`}
                className={`bg-white rounded-2xl overflow-hidden border-2 transition shadow-sm ${
                  isConfirmed ? "border-gold shadow-lg shadow-gold/10" : "border-[#E2E8F0] hover:border-navy/30"
                }`}
              >
                <div className="grid md:grid-cols-[280px_1fr] gap-0">
                  {/* Image */}
                  <div className="relative h-56 md:h-auto bg-sand">
                    <img
                      src={proposalImage(p)}
                      alt={p.boat_model_name}
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = fallbackImageFor(p);
                      }}
                    />
                    <div className="absolute top-3 left-3">
                      <span className="text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full bg-white/95 text-navy">
                        {EN ? "Proposal" : "Proposta"} {idx + 1}
                      </span>
                    </div>
                    {isConfirmed && (
                      <div className="absolute top-3 right-3">
                        <span className="text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full bg-gold text-navy inline-flex items-center gap-1">
                          <Sparkles className="h-3 w-3" /> {EN ? "Selected" : "Selezionata"}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-6 md:p-7">
                    {p.proposal_type && p.proposal_type !== "principale" && (
                      <span className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full bg-gold/15 text-navy border border-gold/40 inline-block mb-3">
                        {p.proposal_type === "cambio_imbarcazione" && "Cambio imbarcazione"}
                        {p.proposal_type === "cambio_date" && "Cambio date"}
                        {p.proposal_type === "cambio_destinazione" && "Cambio destinazione"}
                        {p.proposal_type === "alternativa_economica" && "Alternativa economica"}
                        {p.proposal_type === "alternativa_premium" && "Alternativa premium"}
                      </span>
                    )}
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-wider text-gold font-semibold">{p.boat_category}</p>
                        <h2 className="mt-1 font-serif text-2xl text-navy leading-tight">{p.boat_model_name}</h2>
                        <p className="mt-1 text-sm text-ink/70">{p.charter_company}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] uppercase tracking-wider text-ink/60">{EN ? "Weekly price" : "Prezzo settimanale"}</p>
                        <p className="font-serif text-3xl text-navy">{fmtEur(p.price_eur)}</p>
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-ink">
                      {p.boat_length_ft && <span className="inline-flex items-center gap-1"><Ship className="h-3.5 w-3.5 text-navy/60" /> {p.boat_length_ft} ft</span>}
                      {p.boat_cabins != null && p.boat_cabins > 0 && <span>{p.boat_cabins} {EN ? "cabins" : "cabine"}</span>}
                      {p.boat_berths != null && <span>{p.boat_berths} {EN ? "berths" : "posti letto"}</span>}
                      {p.destination && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-navy/60" /> {p.destination}</span>}
                      {p.departure_port && <span><Anchor className="h-3.5 w-3.5 text-navy/60 inline mr-0.5" /> {EN ? "From" : "Da"} {p.departure_port}</span>}
                      {p.available_dates && <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5 text-navy/60" /> {p.available_dates}</span>}
                      {p.skipper_included === true && <span className="text-green-700 font-medium">{EN ? "Skipper included" : "Skipper incluso"}</span>}
                    </div>

                    {/* Per-person price */}
                    {(() => {
                      const showPerCapita = data.num_participants > 1 && !["family", "company"].includes(data.group_type);
                      if (!showPerCapita) return null;
                      const perCapita = Math.round(p.price_eur / data.num_participants);
                      return (
                        <div className="mt-4 p-3 rounded-lg bg-gold/10 border border-gold/40">
                          <p className="font-serif text-base text-navy">
                            {EN
                              ? <>Only <strong>{fmtEur(perCapita)} per person</strong> for this experience ({data.num_participants} guests).</>
                              : <>Solo <strong>{fmtEur(perCapita)} a persona</strong> per questa esperienza ({data.num_participants} partecipanti).</>}
                          </p>
                        </div>
                      );
                    })()}

                    {p.boat_description && (
                      <p className="mt-4 text-sm text-ink leading-relaxed">{p.boat_description}</p>
                    )}

                    {(p.includes || p.excludes) && (
                      <div className="mt-4 grid sm:grid-cols-2 gap-3">
                        {p.includes && (
                          <div className="p-3 rounded-lg bg-sand/60 border border-[#E5E1D2]">
                            <p className="text-[10px] uppercase tracking-wider text-green-700 font-semibold mb-1">{EN ? "Includes" : "Include"}</p>
                            <p className="text-xs text-ink whitespace-pre-line leading-relaxed">{p.includes}</p>
                          </div>
                        )}
                        {p.excludes && (
                          <div className="p-3 rounded-lg bg-sand/60 border border-[#E5E1D2]">
                            <p className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold mb-1">{EN ? "Excludes" : "Non include"}</p>
                            <p className="text-xs text-ink whitespace-pre-line leading-relaxed">{p.excludes}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* CTA */}
                    <div className="mt-6">
                      {isConfirmed ? (
                        <div className="rounded-lg bg-gold/15 border border-gold/40 p-4 flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-gold shrink-0" />
                          <div>
                            <p className="font-semibold text-navy">{EN ? "You have selected this proposal" : "Hai selezionato questa proposta"}</p>
                            <p className="text-xs text-ink mt-0.5">{EN ? "The Seamatch team will reach out within 24 hours to finalise the booking." : "Il team Seamatch ti contatterà entro 24 ore per finalizzare la prenotazione."}</p>
                          </div>
                        </div>
                      ) : (
                        <Button
                          data-testid={`select-proposal-${p.id}`}
                          onClick={() => confirmInterest(p.id)}
                          disabled={!!alreadyConfirmedId || submittingId === p.id}
                          className="w-full sm:w-auto bg-navy text-white hover:bg-[#06101f] h-12 px-7 rounded-full font-semibold"
                        >
                          {submittingId === p.id ? (EN ? "Sending…" : "Invio…") : (EN ? "I'm interested in this proposal" : "Mi interessa questa proposta")}
                          {submittingId !== p.id && <ArrowRight className="h-4 w-4 ml-2" />}
                        </Button>
                      )}
                      {alreadyConfirmedId && alreadyConfirmedId !== p.id && (
                        <p className="mt-2 text-xs text-ink/60">{EN ? "You have already selected another proposal. Write to us if you'd like to change your choice." : "Hai già selezionato un'altra proposta. Contattaci se vuoi cambiare scelta."}</p>
                      )}
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Footer note */}
      <section className="bg-sand py-12">
        <div className="max-w-3xl mx-auto px-5 lg:px-10 text-center">
          <p className="label-eyebrow">{EN ? "Have questions?" : "Hai domande?"}</p>
          <p className="mt-3 font-serif text-2xl text-navy">{EN ? "We're here to help." : "Siamo qui per aiutarti."}</p>
          <p className="mt-2 text-sm text-ink/70">
            {EN
              ? <>Write to <a href="mailto:info@seamatch.pro" className="text-navy underline">info@seamatch.pro</a> for any clarification on a proposal or to request additional options.</>
              : <>Scrivici a <a href="mailto:info@seamatch.pro" className="text-navy underline">info@seamatch.pro</a> per chiarimenti su una proposta o per richiedere ulteriori opzioni.</>}
          </p>
        </div>
      </section>
    </div>
  );
}
